import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { CalendarRange, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/cn";
import { Tooltip } from "@/components/atoms/Tooltip";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { SlotSettings } from "@/types/availability";
import { PreviewLegend } from "./PreviewLegend";
import { SaveOutcomePanel } from "./SaveOutcomePanel";
import { SlotHeatmap } from "./SlotHeatmap";
import { SlotLanes } from "./SlotLanes";
import { SlotPreviewStats } from "./SlotPreviewStats";
import { COLOR_MODES, type ColorMode } from "./sceneTheme";
import { addDays, diffProjections, projectSlots, startOfWeek } from "./slotProjection";

/**
 * The scene is the only heavy part of this feature, so it is the only part
 * that is code-split: the verdict and the numbers paint immediately from the
 * main bundle while the scene chunk arrives.
 */
const SlotScene3D = lazy(() => import("./SlotScene3D"));

const PROJECTION_DEBOUNCE_MS = 120;

type ViewMode = "plate" | "heat" | "lanes";

const VIEWS: { value: ViewMode; label: string; short: string }[] = [
  { value: "plate", label: "3D plate", short: "3D" },
  { value: "heat", label: "Horizon heatmap", short: "Heatmap" },
  { value: "lanes", label: "Day lanes", short: "Lanes" },
];

interface Props {
  settings: SlotSettings;
  savedSettings: SlotSettings;
  hasSavedBaseline: boolean;
  scopeIncomplete: boolean;
  activeFlag: boolean;
  /** The selected scope keeps its own availability; the default window is moot. */
  windowOptional?: boolean;
}

/**
 * Live visualization of what the viewing settings would generate.
 *
 * Everything is derived client-side: `/api/booking/v1/slots/available` answers
 * from *saved* settings for a real property, so it cannot preview an unsaved
 * edit or a legal-entity-wide scope.
 */
export function SlotPreviewPanel({
  settings,
  savedSettings,
  hasSavedBaseline,
  scopeIncomplete,
  activeFlag,
  windowOptional = false,
}: Props) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [view, setView] = useState<ViewMode>("plate");
  const [colorMode, setColorMode] = useState<ColorMode>("agent");
  const [soloKey, setSoloKey] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef);

  // Typing shouldn't re-lay ~300 tiles per keystroke; the maths is cheap, the
  // layout is not.
  const debouncedSettings = useDebouncedValue(settings, PROJECTION_DEBOUNCE_MS);

  // Pinned once per mount so the notice cutoff can't drift mid-edit and
  // silently change the projection under the operator.
  const now = useMemo(() => new Date(), []);
  const weekStart = useMemo(() => addDays(startOfWeek(now), weekOffset * 7), [now, weekOffset]);

  const projection = useMemo(
    () =>
      projectSlots({
        settings: debouncedSettings,
        weekStart,
        now,
        scopeIncomplete,
        activeFlag,
        ownAvailability: windowOptional,
      }),
    [debouncedSettings, weekStart, now, scopeIncomplete, activeFlag, windowOptional],
  );

  const savedProjection = useMemo(
    () =>
      hasSavedBaseline
        ? projectSlots({ settings: savedSettings, weekStart, now, activeFlag: true })
        : null,
    [savedSettings, weekStart, now, hasSavedBaseline],
  );

  const diff = useMemo(
    () => diffProjections(projection, savedProjection, hasSavedBaseline),
    [projection, savedProjection, hasSavedBaseline],
  );

  // Colouring by agent is useless with a single agent — every tile gets the
  // same hue, which is exactly how the plate ended up reading as one flat
  // colour. Fall back to an encoding that still varies.
  //
  // "How many agents" here means how many the projection actually hands slots to, not how many
  // are assigned: with the round-robin off the whole horizon goes to the top-priority agent, so a
  // ten-agent list still colours as one.
  const listedAgentCount = debouncedSettings.users?.length ?? 0;
  const agentCount = debouncedSettings.roundRobinEnabled ? listedAgentCount : Math.min(listedAgentCount, 1);
  const effectiveMode: ColorMode =
    colorMode === "agent" && agentCount <= 1 ? "time" : colorMode;

  useEffect(() => setSoloKey(null), [effectiveMode, view]);

  return (
    <div ref={containerRef} className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <SaveOutcomePanel
        projection={projection}
        diff={diff}
        hasSavedBaseline={hasSavedBaseline}
      />

      <SlotPreviewStats projection={projection} />

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
        <div className="flex shrink-0 items-center gap-2 border-b border-bridge-border px-3 py-2">
          <CalendarRange size={13} className="shrink-0 text-bridge-accent" />
          <span className="font-mono text-[11px] uppercase tracking-[.12em] text-bridge-muted">
            Live preview
          </span>
          {/* Named tabs rather than icon-only: four chart types are not
              guessable from glyphs, and the header has the room. */}
          <div className="ml-auto flex items-center gap-0.5">
            {VIEWS.map((v) => (
              <button
                key={v.value}
                type="button"
                aria-pressed={view === v.value}
                onClick={() => setView(v.value)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-[13px] font-semibold transition-colors",
                  view === v.value
                    ? "bg-bridge-accent/10 text-bridge-accent"
                    : "text-bridge-muted hover:bg-bridge-bg hover:text-bridge-fg",
                )}
              >
                {v.short}
              </button>
            ))}
          </div>
        </div>

        {/* Week stepper only means something for the week-shaped views. */}
        {view !== "heat" && (
          <div className="flex shrink-0 items-center justify-between border-b border-bridge-border px-2 py-1">
            <button
              type="button"
              aria-label="Previous week"
              onClick={() => setWeekOffset((w) => w - 1)}
              className="rounded p-0.5 text-bridge-muted transition-colors hover:bg-bridge-bg hover:text-bridge-fg"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="font-mono text-[12.5px] font-semibold tabular-nums text-bridge-fg">
              {formatWeekRange(weekStart)}
            </span>
            <button
              type="button"
              aria-label="Next week"
              onClick={() => setWeekOffset((w) => w + 1)}
              className="rounded p-0.5 text-bridge-muted transition-colors hover:bg-bridge-bg hover:text-bridge-fg"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}

        <div className="relative min-h-[300px] flex-1">
          {view === "plate" ? (
            // The scene only mounts once it is on screen — the section can be
            // opened while the operator is looking somewhere else entirely.
            inView ? (
              <Suspense
                fallback={
                  <SlotLanes
                    projection={projection}
                    colorMode={effectiveMode}
                    soloKey={soloKey}
                  />
                }
              >
                <SlotScene3D
                  projection={projection}
                  colorMode={effectiveMode}
                  soloKey={soloKey}
                  muted={scopeIncomplete}
                />
              </Suspense>
            ) : (
              <div className="h-full" />
            )
          ) : view === "heat" ? (
            <SlotHeatmap projection={projection} now={now} />
          ) : (
            <SlotLanes projection={projection} colorMode={effectiveMode} soloKey={soloKey} />
          )}
        </div>

        {/* Colour-by control plus the legend it drives. Heatmap encodes
            magnitude on its own ramp, so the control doesn't apply there. */}
        {view !== "heat" && (
          <div className="shrink-0 space-y-1.5 border-t border-bridge-border px-3 py-2.5">
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-[11px] uppercase tracking-[.12em] text-bridge-subtle">
                Colour by
              </span>
              <div className="flex gap-0.5">
                {COLOR_MODES.map((m) => {
                  const disabled = m.value === "agent" && agentCount <= 1;
                  return (
                    <Tooltip
                      key={m.value}
                      content={
                        disabled
                          ? listedAgentCount > 1
                            ? "Every viewing goes to the top-priority agent — turn on \u201CShare viewings round-robin\u201D to colour by agent"
                            : "Add a second agent to colour by agent"
                          : `Colour tiles by ${m.label.toLowerCase()}`
                      }
                      side="top"
                    >
                      <button
                        type="button"
                        aria-pressed={effectiveMode === m.value}
                        disabled={disabled}
                        onClick={() => setColorMode(m.value)}
                        className={cn(
                          "rounded px-2.5 py-1 text-[12.5px] font-semibold transition-colors",
                          effectiveMode === m.value
                            ? "bg-bridge-accent/10 text-bridge-accent"
                            : "text-bridge-muted hover:text-bridge-fg",
                          disabled && "cursor-not-allowed opacity-40 hover:text-bridge-muted",
                        )}
                      >
                        {m.label}
                      </button>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
            <PreviewLegend
              projection={projection}
              colorMode={effectiveMode}
              soloKey={soloKey}
              onSolo={setSoloKey}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function formatWeekRange(weekStart: Date): string {
  const end = addDays(weekStart, 6);
  const sameMonth = weekStart.getMonth() === end.getMonth();
  const startLabel = weekStart.toLocaleDateString(undefined, {
    day: "numeric",
    ...(sameMonth ? {} : { month: "short" }),
  });
  const endLabel = end.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  return `${startLabel} – ${endLabel}`;
}

/** True once the element has been scrolled into view at least once. */
function useInView(ref: React.RefObject<HTMLElement>): boolean {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setInView(true);
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, inView]);
  return inView;
}
